import { useMutation } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { type SubmitEvent, useState } from 'react'

import type { LoginResponse } from '@/bindings/LoginResponse'
import type { PlatformLoginRequest } from '@/bindings/PlatformLoginRequest'
import type { RestaurantLoginRequest } from '@/bindings/RestaurantLoginRequest'
import { Button } from '@/components/button'
import { Field, Input, PasswordInput } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card } from '@/components/layout'
import { AuthPage } from '@/features/auth/auth-page'
import { emptyIdentifierDraft, readRememberedChannel, rememberIdentifier, toLoginIdentifier } from '@/features/auth/identifier-draft'
import { IdentifierInput } from '@/features/auth/identifier-input'
import { useAfterLogin } from '@/features/auth/use-after-login'
import { useAnonymousOnly } from '@/features/auth/use-anonymous-only'
import { authPath, request } from '@/lib/api'
import { type System, type UserOf } from '@/lib/auth-store'
import { useT } from '@/lib/i18n/translate'
import { REGISTER_PATH, forgotPasswordPathFor, loginPathFor } from '@/lib/return-path'

/**
 * Restaurant login is the Email | Phone toggle (T11.3), opening on the
 * channel last used on this device (email until then); platform login is
 * email-only (T11.8). Success goes through `useAfterLogin` (D1's return-path
 * routing); a live session opening this page is sent to the same place.
 */
export function LoginPage<S extends System>({ system }: { system: S }) {
  const t = useT()
  const search = useSearch({ from: loginPathFor(system) })
  const liveSessionLeave = useAnonymousOnly(system, search)
  const afterLogin = useAfterLogin(system)
  const [identifier, setIdentifier] = useState(() => emptyIdentifierDraft(readRememberedChannel()))
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const login = useMutation({
    mutationFn: (body: RestaurantLoginRequest | PlatformLoginRequest) =>
      request<LoginResponse<UserOf<S>>>(authPath(system, '/login'), { method: 'POST', body }),
    onSuccess: (response) => {
      if (system === 'restaurant') rememberIdentifier(identifier)
      return afterLogin(response)
    },
  })

  if (liveSessionLeave) return liveSessionLeave

  function submit(event: SubmitEvent) {
    event.preventDefault()
    login.mutate(
      system === 'platform'
        ? { email: email.trim(), password }
        : { identifier: toLoginIdentifier(identifier), password },
    )
  }

  return (
    <AuthPage>
      <Card className="flex flex-col gap-4 p-6">
        <h1 className="text-xl font-semibold">{t(system === 'platform' ? 'auth.platformLogin' : 'auth.login')}</h1>
        <form onSubmit={submit} className="flex flex-col gap-4">
          {system === 'platform' ? (
            <Field label={t('auth.email')}>
              <Input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
          ) : (
            <IdentifierInput value={identifier} onChange={setIdentifier} />
          )}
          <Field label={t('auth.password')}>
            <PasswordInput
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <FormError error={login.error} />
          <Button type="submit" loading={login.isPending} block>
            {t('auth.login')}
          </Button>
        </form>
        <div className="flex flex-col items-center gap-2 text-sm">
          <Link to={forgotPasswordPathFor(system)} search={search} className="text-accent-ink font-medium hover:underline">
            {t('auth.forgotPassword')}
          </Link>
          {system === 'restaurant' && (
            <Link to={REGISTER_PATH} search={search} className="text-accent-ink font-medium hover:underline">
              {t('auth.register')}
            </Link>
          )}
        </div>
      </Card>
    </AuthPage>
  )
}
